import { Injectable } from "@angular/core";
import { ApiHttpService } from "./api-http.service";
import { Observable } from "rxjs";
import { RequestBarcodeImage } from "../../view-request/request-barcode/request-barcode.component";
import { DocumentModel } from "./file-saver.service";
import { PollinationTypeEnum } from "./pollination-stake-label.service";

@Injectable()
export class RequestService {
  constructor(private apiHttp: ApiHttpService) { }

  public saveStub(stub: RequestStub): Observable<SavedRequest> {
    return this.apiHttp.post<SavedRequest>(`requests`, stub);
  }

  public saveDetails(details: RequestDetails): Observable<SavedRequest> {
    if (!details.id) {
      return this.saveStub(details).switchMap(r => {
        details.id = r.id;
        return this.putDetails(details);
      });
    } else {
      return this.putDetails(details);
    }
  }

  public saveAssignmentNotes(assignmentNotes: AssignmentNotes): Observable<SavedRequest> {
    return this.apiHttp.put<SavedRequest>(`requests/${assignmentNotes.id}/assignmentdetails`, assignmentNotes);
  }

  public getBarcode(id: number): Observable<RequestBarcodeImage> {
    return this.apiHttp.get<RequestBarcodeImage>(`requests/${id}/barcode`);
  }

  public getPlants(id: number): Observable<Plant[]> {
    return this.apiHttp.get<Plant[]>(`requests/${id}/plants`);
  }

  public getRequestInformation(id: number): Observable<RequestInformation> {
    return this.apiHttp.get<RequestInformation>(`requests/${id}`);
  }

  public getCeLogistics(id: number): Observable<CeLogistics> {
    return this.apiHttp.get<CeLogistics>(`requests/${id}/celogistics`).map(l => {
      return <CeLogistics>{
        ids: l.requestId,
        tacticalLiaisonId: l.tacticalLiaisonId,
        notes: l.notes,
        requestTypeId: l.requestTypeId,
        plantCareTemplateId: l.plantCareTemplateId,
        plannedPlantingDate: l.plannedPlantingDate
          ? new Date(l.plannedPlantingDate)
          : null,
        facilityId: l.facilityId,
        prismExperimentName: l.prismExperimentName,
        aoiId: l.aoiId,
        aoiName: l.aoiName,
        actualPlantDate: l.actualPlantDate
          ? new Date(l.actualPlantDate)
          : null
      };
    });
  }

  public getDetails(id: number): Observable<RequestDetails> {
    return this.apiHttp.get<RequestDetails>(`requests/${id}/details`).map(r => {
      return <RequestDetails>{
        id: r.id,
        requesterId: r.requesterId,
        ceSiteId: r.ceSiteId,
        cropId: r.cropId,
        description: r.description,
        delegateId: r.delegateId,
        productConceptIds: r.productConceptIds,
        programKey: r.programKey,
        projectKey: r.projectKey,
        requestPurposeId: r.requestPurposeId,
        requestedPlantingDate: r.requestedPlantingDate
          ? new Date(r.requestedPlantingDate)
          : null,
        requestedSeedPacketDate: r.requestedSeedPacketDate
          ? new Date(r.requestedSeedPacketDate)
          : null,
        weeksOfColdTreatment: r.weeksOfColdTreatment,
        title: r.title,
        regulatoryStatus: r.regulatoryStatus,
        ppqRequired: r.ppqRequired,
        phytoInspectionRequired: r.phytoInspectionRequired
      };
    });
  }

  public getDesign(id: number): Observable<RequestDesign> {
    return this.apiHttp.get<RequestDesign>(`requests/${id}/design`);
  }

  public saveDesign(design: RequestDesign): Observable<SavedRequest> {
    return this.putDesign(design);
  }

  public submitRequest(id: number): Observable<any> {
    return this.apiHttp.post<any>(`requests/${id}/submissions`);
  }

  public assignRequest(id: number): Observable<SavedRequest> {
    return this.apiHttp.post<any>(`requests/${id}/assignments`);
  }

  public reAssignRequest(requestId: number, tacticalLiaisonId: number): Observable<SavedRequest> {
    return this.apiHttp.put<SavedRequest>(`requests/${requestId}/tacticalliaison/update`, { tacticalLiaisonId });
  }

  public completeRequestPlan(id: number): Observable<SavedRequest> {
    return this.apiHttp.post<any>(`requests/${id}/requestplancompletions`);
  }

  public completeRequestReview(id: number): Observable<SavedRequest> {
    return this.apiHttp.post<any>(`requests/${id}/requestreviews`);
  }

  public createExperiment(id: number): Observable<SavedRequest> {
    return this.apiHttp.post<any>(`requests/${id}/experiments`);
  }

  public rejectRequest(id: number): Observable<SavedRequest> {
    return this.apiHttp.post<any>(`requests/${id}/rejections`);
  }

  public sendBackToPlanning(id: number): Observable<SavedRequest> {
    return this.apiHttp.post<any>(`requests/${id}/requestplanrejections`);
  }

  public getPlantFile(id: number): Observable<DocumentModel> {
    return this.apiHttp.get<DocumentModel>(`requests/${id}/plantfile`);
  }

  public getPlantingCards(requestIds: number[]): Observable<DocumentModel> {
    return this.apiHttp.post<DocumentModel>(`requests/plantingcards`, { requestIds });
  }

  public saveExperimentCreationDetails(details: ExperimentCreationModel) {
    return this.apiHttp.put<any>(`requests/${details.id}/experimentcreationdetails`, details);
  }

  public cloneRequest(id: number): Observable<SavedRequest> {
    return this.apiHttp.post<any>(`requests/${id}/clones`);
  }

  public createDonorRequest(id: number): Observable<SavedRequest> {
    return this.apiHttp.post<any>(`requests/${id}/createDonorRequest`);
  }

  public cancelRequest(id: number): Observable<SavedRequest> {
    return this.apiHttp.post<any>(`requests/${id}/cancellations`);
  }

  private putDesign(design: RequestDesign): Observable<SavedRequest> {
    return this.apiHttp.put<SavedRequest>(
      `requests/${design.id}/design`,
      design
    );
  }

  private putDetails(details: RequestDetails): Observable<SavedRequest> {
    return this.apiHttp.put<any>(`requests/${details.id}/details`, details);
  }
}

export interface SavedRequest {
  id: number;
}

export interface RequestStub {
  title: string;
}

export interface RequestInformation {
  id: number;
  requestNumber: string;
  statusId: number;
}

export interface RequestDetails {
  id?: number;
  requesterId?: number;
  title: string;
  ceSiteId?: number;
  cropId?: number;
  description: string;
  delegateId?: number;
  productConceptIds: number[];
  programKey: string;
  projectKey: string;
  requestedPlantingDate?: Date;
  requestedSeedPacketDate?: Date;
  weeksOfColdTreatment: number;
  requestPurposeId?: number;
  regulatoryStatus?: string;
  ppqRequired?: boolean;
  phytoInspectionRequired?: boolean;
}

export interface RequestDesign {
  id: number;
  numberOfTreatments?: number;
  numberOfReps?: number;
  recurrencePattern: RecurrencePattern;
  recurrenceWeekInterval?: number;
  recurrenceDays?: RecurrenceDays;
  recurrenceEndDate?: Date;
  embryoRescue?: boolean;
  minimumSeedsToReturn?: number;
  numberOfSeedsPerCell: number;
  indexing: boolean;
  harvestPreference: HarvestPreference;
  pollinationInstructions?: PollinationInstructions;
  requestedSeed?: RequestedSeed[];
  treatments?: Treatment[];
  requestedSamplings?: RequestedSampling[];
  seedRequestDesired: boolean;
  originatingSystem: OriginatingSystem;
  sprayStudy: boolean;
  pesticideInstructionId?: number;
  pesticidesAllowedAfterDays?: number;
  pesticideLifeOfPlant: boolean;
}

export enum RecurrencePattern {
  Unknown = 0,
  None = 1,
  Weekly = 2
}

export enum RecurrenceDays {
  None = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 4,
  Thursday = 8,
  Friday = 16,
  Saturday = 32,
  Sunday = 64
}

export enum OriginatingSystem {
  Unknown = 0,
  MyCE = 1,
  EvEnger = 2
}

export interface HarvestPreference {
  standardHarvest: boolean;
  haploidInduction: boolean;
  manyToOne: boolean;
  uniquePedigree: boolean;
}

export interface RequestedSeed {
  requestId?: number;
  entryNumber?: number;
  siid?: number;
  geid?: number;
  geName: string;
  numberOfSeeds?: number;
  regulatoryStatus: string;
  containsColorMarker: boolean;
  treatments: RequestedSeedTreatment[];
}

export interface RequestedSeedTreatment {
  treatmentNumber: number;
  seedCount: number;
}

export interface Treatment {
  requestId?: number;
  treatmentNumber?: number;
  plantingInstructions?: PlantingInstructions;
  flatInstructions?: FlatInstructions;
  pottingInstructions?: PottingInstructions;
  plantHandlingInstructions?: PlantHandlingInstructions;
  sprayBoothStudy?: SprayBoothStudy[];
}

export interface PlantingInstructions {
  requestId?: number;
  treatmentNumber?: number;
  indexing?: boolean;
  seedsToStart?: number;
  plantsToPots?: number;
  plantsToHarvest?: number;
}

export interface FlatInstructions {
  requestId?: number;
  treatmentNumber?: number;
  substrateId: number;
  irrigationMethodId: number;
  fertigationMethodId?: number;
  flatTypeId?: number;
  directSeedToPots: boolean;
}

export interface PottingInstructions {
  requestId?: number;
  treatmentNumber?: number;
  potTypeId?: number;
  substrateId?: number;
  irrigationMethodId?: number;
  fertigationMethodId?: number;
}

export interface PollinationInstructions {
  requestId?: number;
  preferences: PollinationPreferenceGroup[];
  pollinationCategoryId?: number;
  donorsNeeded: boolean;
}

export interface PollinationPreferenceGroup {
  orderNumber: number;
  conjunction: number;
  items: PollinationPreferenceGroupItem[];
}

export interface PollinationPreferenceGroupItem {
  orderNumber: number;
  conjunction: number;
  pollinationTypeId: PollinationTypeEnum;
  geName: string;
}

export interface PlantHandlingInstructions {
  requestId?: number;
  treatmentNumber?: number;
  lightingInstructionId?: number;
  temperatureInstructionId?: number;
  humidityInstructionId?: number;
  imagingNeeded?: boolean;
}

export interface AssignmentNotes {
  id: number;
  tacticalLiaisonId: number;
}

export interface ExperimentCreationModel {
  id: number;
  facilityId: number;
  prismExperimentName: string;
  aoiId: number;
  aoiName: string;
  actualPlantDate: Date;
  trialManagerID: string;
}

export interface CeLogistics {
  requestId?: number;
  tacticalLiaisonId?: number;
  notes: string;
  requestTypeId: number;
  plantCareTemplateId?: number;
  plannedPlantingDate?: Date;
  facilityId?: number;
  prismExperimentName: string;
  aoiId?: number;
  aoiName: string;
  actualPlantDate?: Date;
}

export interface Plant {
  id: number;
  euid: number;
  geid?: number;
}

export interface RequestedSampling {
  requestId?: number;
  order: number;
  laboratoryAssayId: number;
  laboratoryId: number;
  tissueTypeId: number;
  growthStageId?: number;
  tissueCollectionType: TissueCollectionType;
  daysAfterPlanting?: number;
}

export enum TissueCollectionType {
  Standard = 0,
  Custom = 1
}

export interface SprayBoothStudy {
  sprayEventNumber: number;
  applicationVolumeId: number;
  applicationTimepointId: number;
  sprayEventDaysAfterPlanting: number;
  actives: Active[];
  adjuvants: Adjuvant[];
}

export interface Active {
  activeId: number;
  rateValue: number;
  rateUnitId: number;
  relativeFieldRate: number;
}

export interface Adjuvant {
  adjuvantId: number;
  adjuvantValue: number;
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-2/_work/98/s/src/app/shared/services/request.service.ts